import {
    Accordion,
    Alert,
    Badge,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import React from 'react';
import { ImportResultDto } from '../../../models/employeeHub';

interface ImportResultSummaryProps {
    result: ImportResultDto;
}

const getBannerColor = (result: ImportResultDto): string => {
    const processedCount =
        result.employeesCreated +
        result.employeesUpdated +
        result.employeesSkipped +
        result.employeesSoftDeleted;

    if (result.errors.length > 0 && processedCount === 0) {
        return 'red';
    }
    if (result.errors.length > 0) {
        return 'orange';
    }
    if (result.warnings.length > 0) {
        return 'yellow';
    }
    return 'green';
};

const getBannerTitle = (color: string): string => {
    switch (color) {
        case 'red':
            return 'Import Failed';
        case 'orange':
            return 'Import Completed with Errors';
        case 'yellow':
            return 'Import Completed with Warnings';
        default:
            return 'Import Successful';
    }
};

export const ImportResultSummary: React.FC<ImportResultSummaryProps> = ({ result }) => {
    const bannerColor = getBannerColor(result);
    const bannerTitle = getBannerTitle(bannerColor);

    return (
        <Stack gap="md">
            <Alert color={bannerColor} title={bannerTitle} />

            <Stack gap="xs">
                <Title order={5}>Summary</Title>
                <Table withTableBorder withColumnBorders>
                    <Table.Tbody>
                        <Table.Tr>
                            <Table.Td fw={500}>Employees Created</Table.Td>
                            <Table.Td>
                                <Badge color="green" variant="light">{result.employeesCreated}</Badge>
                            </Table.Td>
                            <Table.Td fw={500}>Employees Updated</Table.Td>
                            <Table.Td>
                                <Badge color="brand.5" variant="light">{result.employeesUpdated}</Badge>
                            </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Td fw={500}>Employees Skipped</Table.Td>
                            <Table.Td>
                                <Badge color="gray" variant="light">{result.employeesSkipped}</Badge>
                            </Table.Td>
                            <Table.Td fw={500}>Employees Failed</Table.Td>
                            <Table.Td>
                                <Badge color="red" variant="light">{result.employeesFailed}</Badge>
                            </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Td fw={500}>Employees Soft-Deleted</Table.Td>
                            <Table.Td>
                                <Badge color="orange" variant="light">{result.employeesSoftDeleted}</Badge>
                            </Table.Td>
                            <Table.Td fw={500}>Dictionaries Created</Table.Td>
                            <Table.Td>
                                <Badge color="teal" variant="light">{result.dictionariesCreated}</Badge>
                            </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Td fw={500}>Allocations Opened</Table.Td>
                            <Table.Td>
                                <Badge color="green" variant="light">{result.allocationsOpened}</Badge>
                            </Table.Td>
                            <Table.Td fw={500}>Allocations Closed</Table.Td>
                            <Table.Td>
                                <Badge color="gray" variant="light">{result.allocationsClosed}</Badge>
                            </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Td fw={500}>Allocations Unchanged</Table.Td>
                            <Table.Td>
                                <Badge color="gray" variant="light">{result.allocationsUnchanged}</Badge>
                            </Table.Td>
                            <Table.Td></Table.Td>
                            <Table.Td></Table.Td>
                        </Table.Tr>
                    </Table.Tbody>
                </Table>
            </Stack>

            {result.warnings.length > 0 && (
                <Accordion variant="contained">
                    <Accordion.Item value="warnings">
                        <Accordion.Control>
                            <Text fw={500}>Warnings ({result.warnings.length})</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="xs">
                                {result.warnings.map((warning, index) => (
                                    <Text key={`${warning.type}-${index}`} size="sm" c="dimmed">
                                        [{warning.type}] {warning.message}
                                    </Text>
                                ))}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            )}

            {result.errors.length > 0 && (
                <Accordion variant="contained">
                    <Accordion.Item value="errors">
                        <Accordion.Control>
                            <Text fw={500} c="red">Errors ({result.errors.length})</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="xs">
                                {result.errors.map((error, index) => (
                                    <Text key={`${error.rowNumber}-${index}`} size="sm" c="red">
                                        Row {error.rowNumber}: {error.message}
                                    </Text>
                                ))}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            )}

            {result.skippedEmployees?.length > 0 && (
                <Accordion variant="contained">
                    <Accordion.Item value="skipped">
                        <Accordion.Control>
                            <Text fw={500} c="gray">Skipped Employees ({result.skippedEmployees.length})</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap="xs">
                                {result.skippedEmployees.map((entry, index) => (
                                    <Text key={`${entry.identity}-${index}`} size="sm" c="dimmed">
                                        {entry.identity} ({entry.rows.length} row{entry.rows.length !== 1 ? 's' : ''}: {entry.rows.map(r => `#${r.rowNumber}`).join(', ')})
                                    </Text>
                                ))}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            )}
        </Stack>
    );
};
